import React, { useEffect, useState, useMemo } from "react";
import { api, formatDate, formatApiErrorDetail, API_BASE } from "../lib/api";
import { FileText, Plus, Trash, PaperPlaneTilt, Download, Copy, CheckCircle, BellRinging, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPI = [
  { v: "contratto", label: "Contratto" },
  { v: "nda", label: "NDA / Riservatezza" },
  { v: "lettera_incarico", label: "Lettera d'incarico" },
  { v: "preventivo_aggiuntivo", label: "Preventivo aggiuntivo" },
  { v: "altro", label: "Altro" },
];

const STATO_META = {
  bozza:    { label: "Bozza",      cls: "bg-gray-200 text-gray-800 border-gray-300" },
  inviato:  { label: "Inviato",    cls: "bg-[#0033A0] text-white border-[#0033A0]" },
  firmato:  { label: "Firmato",    cls: "bg-[#008A3C] text-white border-[#008A3C]" },
};

export default function Documenti() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [docsRes, tmplRes, cliRes] = await Promise.all([
        api.get("/documents"),
        api.get("/document-templates"),
        api.get("/clients"),
      ]);
      setItems(docsRes.data || []);
      setTemplates(tmplRes.data || []);
      setClients(cliRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function sendDoc(d) {
    if (!window.confirm(`Inviare "${d.titolo}" per la firma a ${d.cliente_snapshot?.email || "(email mancante)"}?`)) return;
    try {
      const { data } = await api.post(`/documents/${d.id}/send`);
      toast.success("Documento inviato. Link pubblico generato.");
      await load();
      // Copia link
      const url = `${window.location.origin}/d/${data.public_token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    }
  }

  async function copyLink(d) {
    if (!d.public_token) return;
    const url = `${window.location.origin}/d/${d.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato");
    } catch {
      window.prompt("Copia il link:", url);
    }
  }

  async function remindDoc(d) {
    try {
      await api.post(`/documents/${d.id}/remind`);
      toast.success("Promemoria inviato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  async function deleteDoc(d) {
    if (!window.confirm(`Eliminare "${d.titolo}"? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Documento eliminato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  function downloadOriginal(d) {
    const fid = d.file_id;
    window.open(`${API_BASE}/files/${fid}/download?auth=${getAccessToken()}`, "_blank");
  }
  function downloadSigned(d) {
    if (!d.signed_file_id) return;
    window.open(`${API_BASE}/files/${d.signed_file_id}/download?auth=${getAccessToken()}`, "_blank");
  }
  function getAccessToken() {
    // Cookie httpOnly: il backend accetta anche ?auth=. Soluzione semplice: usa /api/files/{id}/download direttamente; ma cookie httpOnly non leggibili da JS. Forzo cookie request via redirect.
    return "";
  }

  const stats = useMemo(() => ({
    totali: items.length,
    firmati: items.filter(d => d.stato === "firmato").length,
    in_attesa: items.filter(d => d.stato === "inviato").length,
    bozze: items.filter(d => d.stato === "bozza").length,
  }), [items]);

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Firma digitale</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Documenti</h1>
          <p className="text-sm text-[#64748B] mt-3">{stats.totali} documenti · {stats.firmati} firmati · {stats.in_attesa} in attesa</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowTemplates(true)} data-testid="btn-templates" className="px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] rounded-sm hover:bg-[#0A0A0A] hover:text-white flex items-center gap-2">
            <FileText size={14} weight="bold" /> Modelli ({templates.length})
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="btn-new-doc" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2">
            <Plus size={14} weight="bold" /> Nuovo Documento
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B] text-sm">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="swiss-card p-12 text-center">
          <FileText size={40} weight="duotone" className="mx-auto text-[#64748B] mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">Nessun documento</h3>
          <p className="text-sm text-[#64748B]">Carica un contratto o crea un modello riutilizzabile.</p>
        </div>
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Titolo</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Tipo</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Inviato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Firmato</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const meta = STATO_META[d.stato] || STATO_META.bozza;
                  return (
                    <tr key={d.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`doc-row-${d.id}`}>
                      <td className="py-3 px-4 font-semibold">{d.titolo}</td>
                      <td className="py-3 px-4 text-[#64748B] capitalize">{(TIPI.find(t=>t.v===d.tipo)?.label) || d.tipo}</td>
                      <td className="py-3 px-4 text-[#64748B]">{d.cliente_snapshot?.ragione_sociale || "—"}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="py-3 px-4 text-[#64748B] text-xs">{d.sent_at ? formatDate(d.sent_at) : "—"}</td>
                      <td className="py-3 px-4 text-[#64748B] text-xs">{d.signed_at ? formatDate(d.signed_at) : "—"}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          {d.stato === "bozza" && d.cliente_snapshot?.email && (
                            <button onClick={() => sendDoc(d)} className="p-1.5 text-[#0033A0] hover:bg-[#0033A0]/10 rounded-sm" title="Invia" data-testid={`send-doc-${d.id}`}><PaperPlaneTilt size={16} weight="bold" /></button>
                          )}
                          {d.public_token && d.stato === "inviato" && (
                            <>
                              <button onClick={() => copyLink(d)} className="p-1.5 text-[#0A0A0A] hover:bg-gray-100 rounded-sm" title="Copia link" data-testid={`copy-link-${d.id}`}><Copy size={16} /></button>
                              <button onClick={() => remindDoc(d)} className="p-1.5 text-[#FF9800] hover:bg-orange-50 rounded-sm" title="Promemoria" data-testid={`remind-${d.id}`}><BellRinging size={16} /></button>
                            </>
                          )}
                          {d.public_token && (
                            <a href={`/d/${d.public_token}`} target="_blank" rel="noreferrer" className="p-1.5 text-[#64748B] hover:text-[#0A0A0A]" title="Apri pubblico"><ArrowSquareOut size={16} /></a>
                          )}
                          {d.stato === "firmato" && (
                            <a href={`${API_BASE}/public/documents/${d.public_token}/file?signed=true`} target="_blank" rel="noreferrer" className="p-1.5 text-[#008A3C] hover:bg-green-50 rounded-sm" title="Scarica firmato" data-testid={`download-signed-${d.id}`}><Download size={16} weight="bold" /></a>
                          )}
                          <button onClick={() => deleteDoc(d)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" title="Elimina"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {items.map((d) => {
              const meta = STATO_META[d.stato] || STATO_META.bozza;
              return (
                <div key={d.id} className="px-4 py-4" data-testid={`doc-card-${d.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{d.titolo}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">{d.cliente_snapshot?.ragione_sociale || "—"}</div>
                    </div>
                    <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="flex gap-2 flex-wrap mt-2">
                    {d.stato === "bozza" && d.cliente_snapshot?.email && (
                      <button onClick={() => sendDoc(d)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] px-2 py-1">Invia</button>
                    )}
                    {d.public_token && d.stato === "inviato" && (
                      <button onClick={() => copyLink(d)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A] px-2 py-1">Copia link</button>
                    )}
                    {d.stato === "firmato" && (
                      <a href={`${API_BASE}/public/documents/${d.public_token}/file?signed=true`} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#008A3C] px-2 py-1">Scarica firmato</a>
                    )}
                    <button onClick={() => deleteDoc(d)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 ml-auto">Elimina</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showCreate && <DocumentCreateModal templates={templates} clients={clients} onClose={() => setShowCreate(false)} onCreated={async () => { setShowCreate(false); await load(); }} />}
      {showTemplates && <TemplatesModal templates={templates} onClose={() => setShowTemplates(false)} onChanged={async () => { await load(); }} />}
    </div>
  );
}

function DocumentCreateModal({ templates, clients, onClose, onCreated }) {
  const [titolo, setTitolo] = useState("");
  const [tipo, setTipo] = useState("contratto");
  const [descrizione, setDescrizione] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function uploadFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.id);
      if (!titolo) setTitolo(f.name.replace(/\.[^.]+$/, ""));
      toast.success("File caricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload");
    } finally {
      setUploading(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (templateId) {
        // Da template
        await api.post(`/documents/from-template/${templateId}`, { cliente_id: clienteId || null, titolo });
      } else {
        if (!fileId) { toast.error("Carica un file"); setSaving(false); return; }
        await api.post("/documents", { titolo, tipo, descrizione: descrizione || null, file_id: fileId, cliente_id: clienteId || null });
      }
      toast.success("Documento creato");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0]">
          <h3 className="font-display text-2xl font-bold">Nuovo Documento</h3>
        </div>
        <form onSubmit={save} className="p-4 sm:p-6 space-y-4">
          {templates.length > 0 && (
            <Field label="Da modello (opzionale)">
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="inp" data-testid="template-select">
                <option value="">— Carica file nuovo —</option>
                {templates.filter(t => t.attivo).map(t => <option key={t.id} value={t.id}>{t.titolo}</option>)}
              </select>
            </Field>
          )}
          {!templateId && (
            <>
              <Field label="Tipo">
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="inp">
                  {TIPI.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="File PDF *">
                <input type="file" accept=".pdf,application/pdf" onChange={uploadFile} className="inp" data-testid="file-input" />
                {uploading && <div className="text-xs text-[#64748B] mt-1">Caricamento…</div>}
                {fileId && <div className="text-xs text-[#008A3C] mt-1">✓ File caricato</div>}
              </Field>
              <Field label="Descrizione">
                <textarea rows={2} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} className="inp" />
              </Field>
            </>
          )}
          <Field label="Titolo *">
            <input required value={titolo} onChange={(e) => setTitolo(e.target.value)} className="inp" data-testid="doc-titolo" placeholder="Es. Contratto SIS-DIG 2026" />
          </Field>
          <Field label="Cliente (per invio firma)">
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="inp" data-testid="cliente-select">
              <option value="">— Nessuno —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale} {c.email ? `· ${c.email}` : ""}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-4 border-t border-[#E2E8F0]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">Annulla</button>
            <button type="submit" disabled={saving} data-testid="save-doc" className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">{saving ? "Salvataggio…" : "Crea Documento"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TemplatesModal({ templates, onClose, onChanged }) {
  const [titolo, setTitolo] = useState("");
  const [tipo, setTipo] = useState("contratto");
  const [descrizione, setDescrizione] = useState("");
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function uploadFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.id);
      if (!titolo) setTitolo(f.name.replace(/\.[^.]+$/, ""));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload");
    } finally { setUploading(false); }
  }

  async function save() {
    if (!fileId || !titolo) { toast.error("Compila titolo e file"); return; }
    try {
      await api.post("/document-templates", { titolo, tipo, descrizione, file_id: fileId, attivo: true });
      setTitolo(""); setDescrizione(""); setFileId(null);
      toast.success("Modello salvato");
      await onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  async function del(t) {
    if (!window.confirm(`Eliminare modello "${t.titolo}"?`)) return;
    try {
      await api.delete(`/document-templates/${t.id}`);
      await onChanged();
      toast.success("Modello eliminato");
    } catch (e) {
      toast.error("Errore");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E2E8F0]">
          <h3 className="font-display text-2xl font-bold">Modelli di Documento</h3>
          <p className="text-xs text-[#64748B] mt-1">Carica una volta sola un modello (es. "Contratto SIS-DIG"), poi riutilizzalo per ogni nuovo cliente.</p>
        </div>
        <div className="p-4 sm:p-6 space-y-4 bg-[#F4F5F7] border-b border-[#E2E8F0]">
          <h4 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0033A0]">+ Aggiungi nuovo modello</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Titolo"><input value={titolo} onChange={(e) => setTitolo(e.target.value)} className="inp" data-testid="tpl-titolo" /></Field>
            <Field label="Tipo">
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="inp">
                {TIPI.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Descrizione"><textarea rows={2} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} className="inp" /></Field>
          <Field label="File PDF">
            <input type="file" accept=".pdf,application/pdf" onChange={uploadFile} className="inp" data-testid="tpl-file" />
            {uploading && <div className="text-xs text-[#64748B] mt-1">Caricamento…</div>}
            {fileId && <div className="text-xs text-[#008A3C] mt-1">✓ File caricato</div>}
          </Field>
          <button onClick={save} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="save-template">Salva Modello</button>
        </div>
        <div className="p-4 sm:p-6 space-y-2">
          <h4 className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">Modelli salvati ({templates.length})</h4>
          {templates.length === 0 ? (
            <div className="text-sm text-[#64748B] py-4">Nessun modello ancora.</div>
          ) : templates.map(t => (
            <div key={t.id} className="flex items-center justify-between gap-3 p-3 border border-[#E2E8F0] rounded-sm">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{t.titolo}</div>
                <div className="text-[11px] text-[#64748B]">{(TIPI.find(x => x.v === t.tipo) || {}).label || t.tipo}</div>
              </div>
              <button onClick={() => del(t)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1">Elimina</button>
            </div>
          ))}
        </div>
        <div className="p-4 flex justify-end border-t border-[#E2E8F0]">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm">Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
